<script setup lang="ts">
import type { CrashRangeStat } from '~/types/crash-pre-game';

type Props = {
  ranges: CrashRangeStat[];
};

const { ranges } = defineProps<Props>();

// "há 8 segundos", "há 2 minutos", "há 1 hora e 25 min". Unidade do tamanho do
// número: "5100 segundos" é tecnicamente certo e não diz nada.
function formatAgo(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`;
  }

  if (seconds < 3600) {
    const minutes = Math.round(seconds / 60);

    return `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  const head = `${hours} ${hours === 1 ? 'hora' : 'horas'}`;

  return minutes ? `${head} e ${minutes} min` : head;
}

// A partir de 10 a casa decimal não informa nada ("a cada ~41 rodadas" é tão
// útil quanto "~40.6"); abaixo dela, 1.9 e 2 são promessas diferentes.
function formatPer(perRounds: number): string {
  return perRounds >= 10
    ? String(Math.round(perRounds))
    : String(Math.round(perRounds * 10) / 10).replace('.', ',');
}

const cards = computed(() =>
  ranges.map((item) => ({
    key: item.range,
    label: `${item.range}X`,
    // Faixa sem frequência é faixa que NÃO saiu na amostra — e isso é uma
    // informação, não um buraco. O card fica apagado, dizendo isso com todas as
    // letras, em vez de mostrar um travessão.
    isCold: item.perRounds === null,
    per: item.perRounds === null ? '' : formatPer(item.perRounds),
    last: item.lastSecondsAgo === null ? null : formatAgo(item.lastSecondsAgo),
    lastSecondsAgo: item.lastSecondsAgo,
  })),
);

// O card que acabou de mudar pisca uma vez. Por `animation`, não `transition`:
// o reset global em assets/css/main.scss zera `transition` no app inteiro.
const updatedKeys = ref<string[]>([]);

const seen = new Map<string, { per: string; lastSecondsAgo: number | null }>();

watch(
  cards,
  (list) => {
    const fresh: string[] = [];

    for (const card of list) {
      const before = seen.get(card.key);

      seen.set(card.key, {
        per: card.per,
        lastSecondsAgo: card.lastSecondsAgo,
      });

      // Primeira leitura só registra: a grade nasce cheia e piscar as seis de
      // uma vez não é atualização, é a tela abrindo.
      if (!before) continue;

      // "há 11 segundos" vira "há 14 segundos" em todo tick sem nada ter
      // acontecido — pulsar nisso seria pisca-pisca permanente. Novidade de
      // verdade é a frequência mudar ou a faixa ter saído OUTRA VEZ, e é isso
      // que o relógio da última andando PRA TRÁS conta.
      const hitAgain =
        card.lastSecondsAgo !== null &&
        (before.lastSecondsAgo === null ||
          card.lastSecondsAgo < before.lastSecondsAgo);

      if (card.per !== before.per || hitAgain) fresh.push(card.key);
    }

    if (fresh.length) updatedKeys.value = fresh;
  },
  { immediate: true },
);

// A marca sai quando a animação acaba: em CSS a animação só dispara quando a
// classe ENTRA, então um card que ficasse marcado não piscaria na próxima vez.
function clearUpdated(key: string): void {
  updatedKeys.value = updatedKeys.value.filter((item) => item !== key);
}
</script>

<template>
  <div class="crash-freqs">
    <div
      v-for="card in cards"
      :key="card.key"
      class="crash-freq"
      :class="{
        'is-cold': card.isCold,
        'is-updated': updatedKeys.includes(card.key),
      }"
      @animationend.self="clearUpdated(card.key)"
    >
      <span
        v-if="!card.isCold"
        class="crash-freq__ribbon"
        >Em média</span
      >

      <span class="crash-freq__r">{{ card.label }}</span>

      <template v-if="card.isCold">
        <p class="crash-freq__big">Não apareceu nenhuma vez</p>
        <p class="crash-freq__last">nas últimas rodadas contadas</p>
      </template>

      <template v-else>
        <p class="crash-freq__big">
          1 <small>a cada</small> ~{{ card.per }} <small>rodadas</small>
        </p>
        <!-- O parágrafo fica no DOM mesmo sem `last`: é ele que segura o rodapé
             do card no lugar e mantém a base alinhada com a do card vizinho. -->
        <p class="crash-freq__last">
          <template v-if="card.last">
            Última há <b>{{ card.last }}</b>
          </template>
        </p>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* `grid-auto-rows: 1fr` iguala a altura dos cards da mesma fileira — sem isso,
   "~1,9" e "9 horas e 46 min" quebram em números de linha diferentes e um card
   fica mais alto que o outro. */
.crash-freqs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  /* row-gap maior que o normal: o ribbon da fileira de baixo invade 10px pra
     cima. O padding abre a mesma folga pra primeira fileira. */
  gap: 20px 8px;
  padding-top: 10px;
}

@media (min-width: 1024px) {
  .crash-freqs {
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 22px 10px;
  }
}

.crash-freq {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 16px 13px 12px;
  border: 1px solid #1c2740;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
}

@media (min-width: 1024px) {
  .crash-freq {
    padding: 18px 16px 14px;
  }
}

/* Fundo sólido na cor do painel: é ele que "corta" a borda do card e faz o chip
   parecer entreposto, e não colado por cima. */
.crash-freq__ribbon {
  position: absolute;
  top: 0;
  left: 50%;
  padding: 2px 10px;
  transform: translate(-50%, -50%);
  border: 1px solid #1c2740;
  border-radius: 999px;
  background: #0f1727;
  color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

.crash-freq__r {
  display: block;
  margin-bottom: 8px;
  color: #4ed8a6;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

/* Reserva duas linhas: "~1,9" e "~114" ocupam o mesmo espaço, então a frase de
   baixo não sobe e desce de card pra card. */
.crash-freq__big {
  min-height: 43px;
  margin: 0;
  color: #e9eef9;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.35;
  font-variant-numeric: tabular-nums;
}

.crash-freq__big small {
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 500;
}

/* Encostado no rodapé do card (`margin-top: auto`) e com duas linhas reservadas:
   "1 minuto" e "9 horas e 46 min" terminam na mesma base. */
.crash-freq__last {
  min-height: 36px;
  margin: auto 0 0;
  padding-top: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  line-height: 1.35;
}

.crash-freq__last b {
  color: #e9eef9;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Atualizou: a borda acende na cor do bloco e o halo se abre pra fora uma vez
   só. O texto novo sobe junto, pra o olho ir pro número que mudou. */
@keyframes crash-freq-flash {
  from {
    border-color: #4ed8a6;
    box-shadow: 0 0 0 0 rgba(78, 216, 166, 0.4);
  }

  to {
    border-color: #1c2740;
    box-shadow: 0 0 0 9px rgba(78, 216, 166, 0);
  }
}

@keyframes crash-freq-text-in {
  from {
    opacity: 0.2;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.crash-freq.is-updated {
  animation: crash-freq-flash 680ms ease-out both;
}

.crash-freq.is-updated .crash-freq__big,
.crash-freq.is-updated .crash-freq__last {
  animation: crash-freq-text-in 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

@media (prefers-reduced-motion: reduce) {
  .crash-freq.is-updated,
  .crash-freq.is-updated .crash-freq__big,
  .crash-freq.is-updated .crash-freq__last {
    animation: none;
  }
}

/* Faixa que não saiu: o card apaga em vez de sumir — "não apareceu" é o dado. */
.crash-freq.is-cold {
  background: none;
}

.crash-freq.is-cold .crash-freq__r,
.crash-freq.is-cold .crash-freq__big {
  color: rgba(255, 255, 255, 0.9);
}

.crash-freq.is-cold .crash-freq__big {
  font-size: 13px;
  font-weight: 600;
}
</style>
